import type { ExpenseCategory, ExpenseItem, InvestmentProperty } from "./models";
import { sum } from "./metrics";

const MONTHS_IN_YEAR = 12;

const applyGrowth = (
  amount: number,
  year: number,
  growthRatePercent: number
): number => amount * Math.pow(1 + growthRatePercent / 100, year - 1);

export const annualCostForExpenseItem = (
  item: ExpenseItem,
  year: number,
  defaultGrowthRatePercent: number
): number => {
  const growthRatePercent = item.growthRate ?? defaultGrowthRatePercent;
  const inflatedAmount = applyGrowth(item.amount, year, growthRatePercent);

  if (item.cadence === "MONTHLY") {
    return inflatedAmount * MONTHS_IN_YEAR;
  }

  if (item.cadence === "ANNUAL") {
    return inflatedAmount;
  }

  const appliesInYears = item.appliesInYears ?? [];
  return appliesInYears.includes(year) ? inflatedAmount : 0;
};

export const annualCostForExpenseCategory = (
  category: ExpenseCategory,
  year: number,
  defaultGrowthRatePercent: number
): number =>
  sum(
    category.items.map((item) =>
      annualCostForExpenseItem(item, year, defaultGrowthRatePercent)
    )
  );

export const annualCostForExpenseCategories = (
  categories: ExpenseCategory[],
  year: number,
  defaultGrowthRatePercent: number
): number =>
  sum(
    categories.map((category) =>
      annualCostForExpenseCategory(category, year, defaultGrowthRatePercent)
    )
  );

export const annualExpenseCategoryTotalsForYear = (
  property: InvestmentProperty,
  year: number
): { id: string; name: string; annualTotal: number }[] => {
  const defaultGrowth = property.expenses.expenseInflationRate;
  return property.expenses.categories.map((category) => ({
    id: category.id,
    name: category.name,
    annualTotal: annualCostForExpenseCategory(category, year, defaultGrowth)
  }));
};

